import { useState, useMemo, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Listing, ProductItem } from '@/lib/types';
import { linkProps } from '@/lib/router';
import { generateDefaultProducts, isProductBusiness } from '@/lib/showcase_generators';

interface ProductEntry {
  product: ProductItem;
  bizName: string;
  bizSlug: string;
  bizCity: string;
  bizPhone: string;
  bizAddress: string;
}

export default function ProductsExplorerPage({
  categoryFilter,
  cityFilter,
  searchQuery
}: {
  categoryFilter?: string;
  cityFilter?: string;
  searchQuery?: string;
}) {
  const [productsList, setProductsList] = useState<ProductEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchQuery || '');
  const [selectedCat, setSelectedCat] = useState(categoryFilter || 'all');
  const [selectedCity, setSelectedCity] = useState(cityFilter || 'all');
  const [selectedProduct, setSelectedProduct] = useState<ProductEntry | null>(null);

  // Seed sample Chennai product businesses
  const sampleProductBusinesses: Listing[] = [
    {
      id: 'biz-nalli-chennai',
      name: 'Nalli Silk Sarees & Bridal Heritage',
      slug: 'nalli-silk-sarees-bridal-heritage-chennai',
      category: 'Saree Retailers',
      city: 'Chennai',
      state: 'Tamil Nadu',
      country: 'India',
      phone: '+91 44 2434 4115',
      email: 'contact@nallisilks.com',
      website: 'https://nalli.com',
      address: 'No. 9, Nageswaran Road, T. Nagar, Chennai',
      latitude: 13.0418,
      longitude: 80.2341,
      description: 'Iconic heritage brand offering pure Kanchipuram silk sarees, bridal collections, Banarasi georgette, and pure zari silks certified with Silk Mark.',
      ai_summary: 'Prestigious handloom silk showroom with 90+ years legacy in Chennai.',
      hero_image_url: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1000&q=80',
      status: 'published',
      pipeline_stage: 'published',
      view_count: 1420,
      call_clicks: 84,
      whatsapp_clicks: 62,
      direction_clicks: 45,
      enquiries: 38,
      faq: [],
      seo: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      schema_json: {
        products: [
          {
            id: 'nalli-p1',
            title: 'Pure Kanchipuram Silk Bridal Saree (Gold Zari Temple Border)',
            category: 'Bridal Sarees',
            price: 14500,
            original_price: 18900,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=800&q=80'],
            description: 'Handwoven pure mulberry silk with rich gold zari pallu and certified Silk Mark guarantee.',
            features: ['Silk Mark Certified', 'Pure Mulberry Silk', 'Unstitched Blouse Piece Included'],
            in_stock: true,
            sku: 'NAL-KANCHI-01',
            badge: 'Silk Mark Certified'
          },
          {
            id: 'nalli-p2',
            title: 'Handcrafted Banarasi Georgette Dual-Tone Meenakari Saree',
            category: 'Party Wear',
            price: 6800,
            original_price: 8500,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?auto=format&fit=crop&w=800&q=80'],
            description: 'Lightweight flowy Banarasi georgette featuring intricate silver and gold floral motifs.',
            features: ['Lightweight & Breathable', 'Intricate Floral Weave', 'Dry Clean Only'],
            in_stock: true,
            sku: 'NAL-BANARAS-02',
            badge: 'Best Seller'
          },
          {
            id: 'nalli-p3',
            title: 'Authentic Traditional Temple Korvai Soft Silk Saree',
            category: 'Traditional Wear',
            price: 4999,
            original_price: 6499,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?auto=format&fit=crop&w=800&q=80'],
            description: 'Comfort-wear soft silk saree with contrasting temple korvai border for pujas and festive occasions.',
            features: ['Soft Silk Feel', 'Contrast Border', 'Easy Draping'],
            in_stock: true,
            sku: 'NAL-KORVAI-03',
            badge: 'Handloom'
          }
        ]
      }
    },
    {
      id: 'biz-chekku-chennai',
      name: 'Grama Chekku Natural Cold Pressed Oils & Spices',
      slug: 'grama-chekku-natural-cold-pressed-oils-spices-chennai',
      category: 'Oil Wholesalers',
      city: 'Chennai',
      state: 'Tamil Nadu',
      country: 'India',
      phone: '+91 94440 12345',
      email: 'orders@gramachekku.in',
      website: 'https://gramachekku.in',
      address: '2nd Avenue, Anna Nagar, Chennai',
      latitude: 13.0850,
      longitude: 80.2101,
      description: 'Traditional wooden churner (Mara Chekku) cold pressed oils extracted from pesticide-free farm produce.',
      ai_summary: 'Pure cold pressed groundnut, sesame, and coconut oil manufacturer in Anna Nagar.',
      hero_image_url: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&w=1000&q=80',
      status: 'published',
      pipeline_stage: 'published',
      view_count: 980,
      call_clicks: 52,
      whatsapp_clicks: 74,
      direction_clicks: 31,
      enquiries: 46,
      faq: [],
      seo: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      schema_json: {
        products: [
          {
            id: 'oil-p1',
            title: 'Wood Pressed (Mara Chekku) Pure Groundnut Oil - 5 Litres Tin',
            category: 'Cooking Oils',
            price: 1450,
            original_price: 1750,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&w=800&q=80'],
            description: 'Zero heat extraction from sun-dried Saurashtra groundnuts. Retains natural flavor and antioxidants.',
            features: ['100% Cold Pressed', 'Unfiltered & Chemical Free', 'Rich in Vitamin E'],
            in_stock: true,
            sku: 'GC-GND-5L',
            badge: '100% Pure'
          },
          {
            id: 'oil-p2',
            title: 'Artisanal Cold Pressed Gingelly Sesame Oil (Palm Jaggery Infused) - 1 Litre',
            category: 'Cooking Oils',
            price: 395,
            original_price: 480,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=800&q=80'],
            description: 'Traditional sesame oil made with pure black sesame seeds and organic Karupatti (palm jaggery).',
            features: ['Natural Palm Jaggery Infused', 'Authentic Aroma', 'Heart Healthy'],
            in_stock: true,
            sku: 'GC-SES-1L',
            badge: 'Best Seller'
          },
          {
            id: 'oil-p3',
            title: 'Pure Single-Origin Alleppey Finger Turmeric Powder (Curcumin 5%+) - 500g',
            category: 'Traditional Spices',
            price: 180,
            original_price: 220,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1615485290382-441e4d049cb5?auto=format&fit=crop&w=800&q=80'],
            description: 'Stone ground Alleppey turmeric with exceptional high natural curcumin for cooking and immunity.',
            features: ['High Curcumin (>5%)', 'No Artificial Color', 'Lab Tested'],
            in_stock: true,
            sku: 'GC-TURM-500',
            badge: 'Organic'
          }
        ]
      }
    },
    {
      id: 'biz-earth-chennai',
      name: 'Earth Essentials Organic Botanicals & Handmade Soaps',
      slug: 'earth-essentials-organic-botanicals-handmade-soaps-chennai',
      category: 'Cosmetic Dealers',
      city: 'Chennai',
      state: 'Tamil Nadu',
      country: 'India',
      phone: '+91 98400 67890',
      email: 'care@earthessentials.com',
      website: 'https://earthessentials.com',
      address: 'Besant Avenue Road, Adyar, Chennai',
      latitude: 13.0012,
      longitude: 80.2565,
      description: 'Artisanal cold processed soaps, essential oils, and herbal hair care handcrafted in small batches.',
      ai_summary: 'Organic bath, skin, and hair care boutique in Adyar, Chennai.',
      hero_image_url: 'https://images.unsplash.com/photo-1607006314045-817e0b571120?auto=format&fit=crop&w=1000&q=80',
      status: 'published',
      pipeline_stage: 'published',
      view_count: 850,
      call_clicks: 41,
      whatsapp_clicks: 89,
      direction_clicks: 22,
      enquiries: 34,
      faq: [],
      seo: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      schema_json: {
        products: [
          {
            id: 'soap-p1',
            title: 'Activated Bamboo Charcoal & Tea Tree Handmade Detox Soap Bar',
            category: 'Handmade Soaps',
            price: 249,
            original_price: 349,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1607006314045-817e0b571120?auto=format&fit=crop&w=800&q=80'],
            description: 'Cold-processed natural soap bar with activated charcoal and pure Australian tea tree oil.',
            features: ['100% Vegan', 'Paraben & SLS Free', 'Deep Skin Detox'],
            in_stock: true,
            sku: 'EE-CHAR-01',
            badge: 'Handmade'
          },
          {
            id: 'soap-p2',
            title: 'Fresh Goat Milk & Kashmiri Saffron Glow Bath Bar',
            category: 'Handmade Soaps',
            price: 299,
            original_price: 399,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1608248597359-25f02fef9723?auto=format&fit=crop&w=800&q=80'],
            description: 'Ultra-nourishing bath bar made with organic goat milk and mongra saffron strands.',
            features: ['Skin Brightening', 'Intense Moisture', 'Zero Fragrance Added'],
            in_stock: true,
            sku: 'EE-SAFF-02',
            badge: 'Best Seller'
          },
          {
            id: 'soap-p3',
            title: 'Ayurvedic Bhringraj & Rosemary Scalp Therapy Hair Growth Oil (200ml)',
            category: 'Hair Care',
            price: 499,
            original_price: 699,
            currency: '₹',
            images: ['https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?auto=format&fit=crop&w=800&q=80'],
            description: 'Slow-infused herbal therapy oil with pure Bhringraj, Rosemary, Amla, and Hibiscus in sesame oil.',
            features: ['Hair Fall Control', 'Non-Sticky Formula', '100% Natural Herbs'],
            in_stock: true,
            sku: 'EE-HAIR-03',
            badge: 'Trending'
          }
        ]
      }
    }
  ];

  useEffect(() => {
    setLoading(true);
    void (async () => {
      let dbListings: Listing[] = [];
      try {
        const { data } = await supabase
          .from('listings')
          .select('*')
          .eq('status', 'published')
          .limit(100);
        if (data && data.length > 0) dbListings = data as Listing[];
      } catch {}

      const localStr = localStorage.getItem('localhub_owner_listings');
      const localListings: Listing[] = localStr ? JSON.parse(localStr) : [];

      const combined = [...sampleProductBusinesses, ...localListings, ...dbListings];
      const map = new Map<string, Listing>();
      combined.forEach(l => {
        if (l && l.id && !map.has(l.id)) map.set(l.id, l);
      });

      const extractedProducts: ProductEntry[] = [];

      Array.from(map.values()).forEach(biz => {
        const s = (biz.schema_json as any) || {};
        let prods: ProductItem[] = [];
        if (Array.isArray(s.products) && s.products.length > 0) {
          prods = s.products;
        } else if (isProductBusiness(biz.category)) {
          prods = generateDefaultProducts(biz.name, biz.category || 'Products', biz.city || 'India');
        }

        prods.forEach(p => {
          extractedProducts.push({
            product: p,
            bizName: biz.name,
            bizSlug: biz.slug || '',
            bizCity: biz.city || 'India',
            bizPhone: biz.phone || '',
            bizAddress: biz.address || ''
          });
        });
      });

      setProductsList(extractedProducts);
      setLoading(false);
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set<string>();
    productsList.forEach(p => {
      if (p.product.category) set.add(p.product.category);
    });
    return Array.from(set);
  }, [productsList]);

  const cities = useMemo(() => {
    const set = new Set<string>();
    productsList.forEach(p => {
      if (p.bizCity) set.add(p.bizCity);
    });
    return Array.from(set);
  }, [productsList]);

  const filteredProducts = useMemo(() => {
    return productsList.filter(item => {
      const matchSearch = !search.trim() ||
        item.product.title.toLowerCase().includes(search.toLowerCase()) ||
        item.bizName.toLowerCase().includes(search.toLowerCase()) ||
        item.product.description.toLowerCase().includes(search.toLowerCase());

      const matchCat = selectedCat === 'all' || item.product.category?.toLowerCase() === selectedCat.toLowerCase();
      const matchCity = selectedCity === 'all' || item.bizCity?.toLowerCase() === selectedCity.toLowerCase();

      return matchSearch && matchCat && matchCity;
    });
  }, [productsList, search, selectedCat, selectedCity]);

  return (
    <div className="w-full max-w-container-max mx-auto px-4 md:px-6 py-8">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 rounded-3xl p-8 md:p-12 text-white shadow-xl mb-8 relative overflow-hidden text-left">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent pointer-events-none" />
        <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider">
          🛍️ Zha Search Marketplace & Storefronts
        </span>
        <h1 className="font-display text-3xl md:text-5xl font-extrabold mt-3 mb-2 leading-tight">
          Explore Verified Local Products & Goods
        </h1>
        <p className="text-emerald-100/80 text-sm md:text-base font-medium max-w-2xl">
          Order handcrafted soaps, Kanchipuram silk sarees, cold pressed oils, spices, gadgets, and apparel directly from verified Indian merchants with instant WhatsApp checkout.
        </p>

        {/* Search Bar */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <span className="material-symbols-outlined absolute left-3.5 top-3 text-slate-400">search</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products (e.g. Silk Sarees, Cold Pressed Oil, Soaps, Spices)..."
              className="w-full h-12 pl-11 pr-4 bg-white/95 text-slate-900 font-bold text-xs rounded-2xl outline-none focus:ring-2 focus:ring-emerald-400 shadow-md placeholder:text-slate-400"
            />
          </div>
          <button
            onClick={() => {}}
            className="h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-2xl shadow-lg transition cursor-pointer flex items-center justify-center gap-1.5 shrink-0"
          >
            <span className="material-symbols-outlined text-base">filter_list</span> Search Catalog
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto w-full pb-2">
          <button
            onClick={() => setSelectedCat('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition cursor-pointer ${
              selectedCat === 'all'
                ? 'bg-emerald-700 text-white shadow-md'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            All Categories ({productsList.length})
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCat(cat)}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition cursor-pointer ${
                selectedCat.toLowerCase() === cat.toLowerCase()
                  ? 'bg-emerald-700 text-white shadow-md'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* City Selector */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-bold text-slate-500">City:</span>
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="h-10 px-3 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 outline-none cursor-pointer"
          >
            <option value="all">All Cities</option>
            {cities.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin text-4xl text-emerald-600 mb-2">⏳</div>
          <p className="text-xs font-bold text-slate-500">Loading Verified Product Catalog...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 border border-dashed border-slate-200 rounded-3xl">
          <span className="material-symbols-outlined text-5xl text-slate-400 mb-2">inventory_2</span>
          <h3 className="font-bold text-sm text-slate-800">No products match your search.</h3>
          <p className="text-xs text-slate-500 mt-1">Try changing category filters or search query.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 text-left">
          {filteredProducts.map((item, idx) => {
            const p = item.product;
            const discount = p.original_price && p.original_price > p.price
              ? Math.round(((p.original_price - p.price) / p.original_price) * 100)
              : null;
            const img = p.images?.[0] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80';

            return (
              <div
                key={`${p.id}-${idx}`}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col group justify-between"
              >
                <div>
                  <a
                    {...linkProps(`/product/${p.slug || p.id || encodeURIComponent(p.title)}`)}
                    className="relative aspect-square overflow-hidden bg-slate-100 block cursor-pointer"
                  >
                    <img
                      src={img}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {p.badge && (
                      <span className="absolute top-2.5 left-2.5 bg-emerald-600 text-white font-extrabold text-[9px] px-2 py-0.5 rounded shadow-sm uppercase tracking-wider">
                        {p.badge}
                      </span>
                    )}
                    {discount && (
                      <span className="absolute top-2.5 right-2.5 bg-red-600 text-white font-black text-[10px] px-1.5 py-0.5 rounded shadow-sm">
                        {discount}% OFF
                      </span>
                    )}
                  </a>

                  <div className="p-4 space-y-2">
                    <div className="flex items-center justify-between text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                      <span>{p.category || 'Product'}</span>
                      <span className="text-emerald-700 font-semibold">{item.bizCity}</span>
                    </div>

                    <a
                      {...linkProps(`/product/${p.slug || p.id || encodeURIComponent(p.title)}`)}
                      className="block cursor-pointer"
                    >
                      <h3 className="font-bold text-xs text-slate-900 line-clamp-2 leading-snug group-hover:text-emerald-700 transition-colors">
                        {p.title}
                      </h3>
                    </a>

                    <a
                      {...linkProps(`/b/${item.bizSlug}`)}
                      className="inline-block text-[11px] font-semibold text-slate-500 hover:text-emerald-700 truncate max-w-full"
                    >
                      Sold by: <span className="font-bold underline">{item.bizName}</span>
                    </a>

                    <div className="pt-2 border-t border-slate-100 flex items-baseline justify-between">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-base font-black text-slate-900">
                          {p.currency || '₹'}{p.price.toLocaleString('en-IN')}
                        </span>
                        {p.original_price && p.original_price > p.price && (
                          <span className="text-[11px] text-slate-400 line-through font-semibold">
                            {p.currency || '₹'}{p.original_price.toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>
                      <span className={`text-[10px] font-bold ${p.in_stock ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {p.in_stock ? '● In Stock' : '● Made to Order'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 pt-0 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setSelectedProduct(item)}
                    className="w-full py-2 bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-200 font-bold text-[11px] rounded-xl transition cursor-pointer"
                  >
                    Quick View
                  </button>
                  <a
                    href={`https://wa.me/${item.bizPhone.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${item.bizName}, I would like to order "${p.title}" (₹${p.price}) seen on ZhaSearch Products Directory.`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded-xl shadow-xs transition flex items-center justify-center gap-1 cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-[13px]">chat</span> Order
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick View Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="bg-white border border-slate-200 rounded-3xl p-6 w-full max-w-xl shadow-2xl space-y-4 text-slate-800 text-left max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                  {selectedProduct.product.category || 'Product'} • {selectedProduct.bizCity}
                </span>
                <h3 className="font-bold text-slate-900 text-base">{selectedProduct.product.title}</h3>
              </div>
              <button onClick={() => setSelectedProduct(null)} className="text-slate-400 hover:text-slate-600 text-lg font-bold">✕</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="aspect-square rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
                <img
                  src={selectedProduct.product.images?.[0] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80'}
                  alt={selectedProduct.product.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-black text-slate-900">
                      {selectedProduct.product.currency || '₹'}{selectedProduct.product.price.toLocaleString('en-IN')}
                    </span>
                    {selectedProduct.product.original_price && selectedProduct.product.original_price > selectedProduct.product.price && (
                      <span className="text-xs text-slate-400 line-through font-semibold">
                        {selectedProduct.product.currency || '₹'}{selectedProduct.product.original_price.toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] font-bold text-emerald-700 mt-0.5">
                    {selectedProduct.product.in_stock ? '● In Stock • Ready for Dispatch' : '● Made to Order'}
                  </div>

                  <div className="text-xs text-slate-600 mt-2">
                    Sold by: <strong>{selectedProduct.bizName}</strong> ({selectedProduct.bizCity})
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed mt-2">
                    {selectedProduct.product.description}
                  </p>
                </div>

                <a
                  href={`https://wa.me/${selectedProduct.bizPhone.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${selectedProduct.bizName}, I would like to order "${selectedProduct.product.title}" (₹${selectedProduct.product.price}) from ZhaSearch.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-sm">chat</span> Instant WhatsApp Checkout
                </a>
              </div>
            </div>

            {selectedProduct.product.features && selectedProduct.product.features.length > 0 && (
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Product Highlights</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {selectedProduct.product.features.map((feat, i) => (
                    <div key={i} className="bg-slate-50 border border-slate-200 p-2.5 rounded-xl text-xs font-semibold text-slate-700 flex items-center gap-2">
                      <span className="text-emerald-600 font-bold">✓</span> {feat}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
